import { CommonModule, UpperCasePipe } from '@angular/common';
import { Component, computed, ElementRef, inject, Injector, Input, ViewChild, AfterViewInit, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonToggleChange, MatButtonToggleGroup, MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatError } from '@angular/material/form-field';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { GridComponent } from 'app/shared/components/grid/grid.component';
import {
  ModalButtonType,
  ModalService,
  ModalComponentInterface,
  ModalConfig,
  ModalInterface,
} from 'app/shared/components/modal';
import { ITransactionSummary } from '../../../transactions/transaction.model';
import { EventEmitter } from '@angular/core';
import { TransactionsService } from 'app/features/transactions/services/transactions.service';
import { BehaviorSubject, Observable, of, switchMap, tap } from 'rxjs';
import { CustomerTransactionsConfig } from './customer-details-transactions-config';
import { GridViewType } from 'app/shared/components/grid/grid-config';
import { FormsModule } from '@angular/forms';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { CustomerEditorComponent } from '../customer-editor/customer-editor.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { CapitalizePipe } from 'app/shared/pipes/capitalize.pipe';
import { DashCasePipe } from 'app/shared/pipes/dash-case.pipe';
import { TemplateRef } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { PermissionService } from '../../../../core/services/permission.service';

import { ScheduledPaymentModel } from '../../models/scheduled-payment.model';
import { CustomerScheduledPaymentsConfig } from './customer-details-scheduled-payments-config';
import { ScheduledPaymentsService } from '../../services/scheduled-payments.service';
import { ScheduledPaymentFormComponent } from '../scheduled-payment-form/scheduled-payment-form.component';
import { NgIf } from '@angular/common';
import { UserStore } from 'app/features/user/store/user.store';
import { PaymentMethodModel, PaymentMethodMode, AchMethodModel, InvoiceModel } from '../../customer.model';
import { UserDetail } from '../../../admin/admin.model';
import { achPaymentMethodActions, paymentMethodActions } from 'app/features/payment/config/actions';
import { GridColumnActionType } from 'app/shared/components/grid/grid-config';
import { describeAccount } from 'app/features/payment/components/payment-collect/payment-methods/account-card-view';
import {
  AccountCardComponent,
  AccountCardItem,
} from 'app/features/payment/components/payment-collect/payment-methods/account-card.component';
import { CardDetailsFormComponent, cardDeclinedMessage } from 'app/features/payment/components/card-details-form/card-details-form.component';
import { AchDetailsFormComponent } from 'app/features/payment/components/ach-details-form/ach-details-form.component';
import { CustomersService } from '../../services/customers.service';
import { CryptService } from 'app/shared/services/crypt.service';
import { NotificationService } from 'app/shared/services/notification.service';
import { OverlayService } from 'app/shared/services/overlay.service';
import { ActionsService } from '../../services/actions.service';
import { Router } from '@angular/router';
import { CustomerInvoicesConfig } from './customer-details-invoices-config';
import { CustomerInvoiceHistoryComponent } from '../customer-invoice-history/customer-invoice-history.component';
import { CustomerSummaryComponent } from '../customer-summary/customer-summary.component';
import { CustomerLedgerComponent } from '../customer-ledger/customer-ledger.component';
import {
  PaymentHistoryComponent,
  type PaymentNavigationRequest,
} from 'features/transactions/pages/payment-history/payment-history.component';
import { CustomerPortalAccountsConfig } from './customer-details-portal-accounts-config';

interface PaymentMethodModelWithForm extends PaymentMethodModel {
  isDefaultFormControl: FormControl<boolean | null>;
}

interface AchMethodModelWithForm extends AchMethodModel {
  isDefaultFormControl: FormControl<boolean | null>;
}

@Component({
  selector: 'app-customer-details',
  standalone: true,
  imports: [
    MatButtonModule,
    MatFormFieldModule,
    CommonModule,
    GridComponent,
    FormsModule,
    MatInputModule,
    MatCheckboxModule,
    MatIconModule,
    ReactiveFormsModule,
    MatButtonToggleModule,
    CustomerEditorComponent,
    CapitalizePipe,
    MatTooltipModule,
    NgIf,
    DashCasePipe,
    UpperCasePipe,
    HasPermissionDirective,
    CustomerInvoiceHistoryComponent,
    CustomerSummaryComponent,
    AccountCardComponent,
    CustomerLedgerComponent,
    PaymentHistoryComponent,
  ],
  providers: [provideNgxMask()],
  templateUrl: './customer-details.component.html',
  host: { class: 'app-customer-details' },
  styles: [
    `
      /* Matches the tone of the SHIP-TO row badge on the Customers grid, so the two
         read as the same fact stated twice rather than two unrelated warnings. */
      .app-customer-details__ship-to-notice {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        margin: 0 0 12px;
        padding: 10px 14px;
        border: 1px solid #f2b8b2;
        border-radius: 6px;
        background: #fdeceb;
        color: #8c2f26;
        font-size: 0.9rem;
      }
    `,
  ],
})
export class CustomerDetailsComponent implements ModalInterface, OnInit, AfterViewInit {
  /** The horizontally scrollable tab strip, so the selected tab can be brought into view. */
  @ViewChild('tabStrip', { read: ElementRef }) tabStrip?: ElementRef<HTMLElement>;
  @ViewChild('tabStrip', { read: MatButtonToggleGroup }) tabGroup?: MatButtonToggleGroup;
  @ViewChild(CustomerEditorComponent) editor?: CustomerEditorComponent;
  @ViewChild('transactionsGrid') transactionsGrid!: GridComponent<ITransactionSummary>;
  @ViewChild('voidTemplate') voidTemplate: TemplateRef<any>;
  @ViewChild('settledTemplate') settledTemplate: TemplateRef<any>;
  @ViewChild('flagsTemplate') flagsTemplate: TemplateRef<any>;
  @ViewChild('tranTypeTemplate') tranTypeTemplate: TemplateRef<any>;
  @ViewChild('transIdTemplate') transIdTemplate: TemplateRef<any>;
  @ViewChild('statusTemplate') statusTemplate: TemplateRef<any>;
  @ViewChild('cardTypeTemplate') cardTypeTemplate: TemplateRef<any>;
  @ViewChild('setDefaultTemplate') setDefaultTemplate: TemplateRef<any>;
  @ViewChild('invoiceStatusTemplate') invoiceStatusTemplate: TemplateRef<any>;
  @ViewChild('invoiceActionsTemplate') invoiceActionsTemplate: TemplateRef<any>;

  public dismiss = null;
  public confirm = null;
  public parentModal?: ModalComponentInterface;

  currentView:
    | 'dashboard'
    | 'editor'
    | 'transactions'
    | 'flashpay'
    | 'updateRequests'
    | 'accounts'
    | 'scheduledPayments'
    | 'invoices'
    | 'ledger'
    | 'portalAccounts' = 'transactions';
  private pendingInitialView?:
    | 'dashboard'
    | 'editor'
    | 'transactions'
    | 'flashpay'
    | 'updateRequests'
    | 'accounts'
    | 'scheduledPayments'
    | 'invoices'
    | 'ledger'
    | 'portalAccounts';

  customerId!: string;
  customerData?: any;

  private paymentMethodsSubject = new BehaviorSubject<PaymentMethodModelWithForm[]>([]);
  private achMethodsSubject = new BehaviorSubject<AchMethodModelWithForm[]>([]);

  transactions$: Observable<ITransactionSummary[]> = of([]);
  requests$: Observable<any[]> = of([]);
  scheduledPayments$: Observable<ScheduledPaymentModel[]> = of([]);
  paymentMethods$: Observable<PaymentMethodModelWithForm[]> = this.paymentMethodsSubject.asObservable();
  achMethods$: Observable<AchMethodModelWithForm[]> = of([]);
  invoices$: Observable<InvoiceModel[]> = of([]);
  portalUsers$: Observable<UserDetail[]> = of([]);

  transactionsConfig: GridViewType<ITransactionSummary> = CustomerTransactionsConfig;
  scheduledPaymentsConfig: GridViewType<ScheduledPaymentModel> = CustomerScheduledPaymentsConfig;

  /**
   * The Accounts on File tab, drawn with the same account cards Collect Payment uses.
   *
   * The rail is taken from which list a row came from, not from the row: ACH accounts are served by
   * a separate endpoint that does not always stamp `tokenType`, and `describeAccount` reads it.
   */
  private readonly cardRows = toSignal(this.paymentMethodsSubject, { initialValue: [] });
  private readonly achRows = toSignal(this.achMethodsSubject, { initialValue: [] });
  readonly accountCards = computed(() =>
    this.toAccountItems(this.cardRows(), 'CARD')
  );
  readonly achAccountCards = computed(() =>
    this.toAccountItems(this.achRows(), 'ACH')
  );

  /** Handed to the shared row-action handlers, which reach this view as `component.parent`. */
  readonly accountActionHost = { parent: this };
  invoicesConfig: GridViewType<InvoiceModel> = CustomerInvoicesConfig;
  portalAccountsConfig: GridViewType<UserDetail> = {} as GridViewType<UserDetail>; // Will be set in ngOnInit

  /**
   * Set directly by `ModalService` when opened as a bottom sheet, and bound as an input when the
   * same component is hosted as a page (`/customers/:customerId`). An input rather than only a
   * property so the page can hand it over declaratively; the modal path is unchanged.
   */
  @Input() data!: ModalConfig;

  private userStore = inject(UserStore);
  private isDefaultFormArray = new FormArray<FormGroup>([]);
  private achDefaultFormArray = new FormArray<FormGroup>([]);

  // Getter to check if customer has a default payment method
  get hasDefaultPaymentMethod(): boolean {
    const paymentMethods = this.paymentMethodsSubject.getValue();
    const achMethods = this.achMethodsSubject.getValue();
    const hasDefaultCard = paymentMethods.some((pm) => pm.isDefault === true);
    const hasDefaultACH = achMethods.some((am) => am.isDefault === true);
    return hasDefaultCard || hasDefaultACH;
  }

  // Getter to check if customer has any payment methods
  get hasAnyPaymentMethods(): boolean {
    const paymentMethods = this.paymentMethodsSubject.getValue();
    const achMethods = this.achMethodsSubject.getValue();
    return paymentMethods.length > 0 || achMethods.length > 0;
  }

  constructor(
    protected injector: Injector,
    protected transactionApiService: TransactionsService,
    protected scheduledPaymentsService: ScheduledPaymentsService,
    protected modalService: ModalService,
    protected customersService: CustomersService,
    protected cryptService: CryptService,
    protected notificationService: NotificationService,
    protected overlayService: OverlayService,
    protected actionsService: ActionsService,
    private permissionService: PermissionService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Keyed on the config itself, not its title: the sheet and the page no longer carry a title
    // (the summary bar above the tabs names the customer), and this block used to be skipped
    // without one — leaving an empty shell.
    if (this.data) {
      this.customerId = this.data.data?.customerId;

      // Use customerData if provided (from action buttons)
      if (this.data.data?.customerData) {
        this.customerData = this.data.data.customerData;
        console.log('[CustomerDetails] Using provided customer data:', this.customerData);
      }

      // Store initial view to apply after templates are ready. 'summary' was the Dashboard tab's
      // old name and is still accepted, since an embedding application may have it in a link.
      const initialView = this.data.data?.initialView;
      if (initialView) {
        this.pendingInitialView = CustomerDetailsComponent.canonicalView(initialView);
      }

      if (this.customerId) {
        // Load customer data from API if not already provided
        if (!this.customerData) {
          this.loadCustomerData();
        }
        this.loadTransactions();
        this.loadScheduledPayments();
        this.loadPaymentMethods();
        this.loadAchPaymentMethods();
        this.loadInvoices();
        this.loadPortalUsers();
      }

      // Dashboard is the default tab. Its cards are all invoice figures, so it exists only where
      // invoicing is enabled; Customer Payments is the default otherwise.
      this.currentView = this.isInvoiceEnabled() ? 'dashboard' : 'transactions';

      // Initialize portal accounts config
      this.portalAccountsConfig = CustomerPortalAccountsConfig;
    }
  }

  /**
   * Whether a tab exists to switch to.
   *
   * Several tabs are hidden until `customerData` has loaded, and `MatButtonToggleGroup` DISCARDS
   * a value that matches no toggle — writing `undefined` back through the two-way binding and
   * leaving the view blank with nothing selected. So a requested view is held until its tab is
   * really there. The modal path never hit this because it is handed `customerData` up front;
   * the page path fetches it.
   */
  private isViewAvailable(view: NonNullable<typeof this.pendingInitialView>): boolean {
    switch (view) {
      case 'dashboard':
      case 'invoices':
        return this.isInvoiceEnabled();
      case 'ledger':
        return this.canViewLedger();
      case 'accounts':
        return this.isAccountsOnFileEnabled();
      case 'scheduledPayments':
        return this.isScheduledPaymentsEnabled();
      case 'portalAccounts':
        return this.isCustomerPortalEnabled();
      default:
        // 'editor' and 'transactions' are always rendered (the editor behind a permission the
        // route already required).
        return true;
    }
  }

  /**
   * A requested view under its current name. Retired tab names are still accepted, because an
   * embedding application or an older caller may have them in a link: 'summary' became the
   * Dashboard, and 'cards' / 'ach' became the one Accounts on File tab.
   */
  static canonicalView(view: string): NonNullable<CustomerDetailsComponent['pendingInitialView']> {
    switch (view) {
      case 'summary':
        return 'dashboard';
      case 'cards':
      case 'ach':
        return 'accounts';
      default:
        return view as NonNullable<CustomerDetailsComponent['pendingInitialView']>;
    }
  }

  /** Apply the requested initial view once its tab exists; otherwise keep waiting. */
  private applyPendingInitialView(): void {
    const view = this.pendingInitialView;
    if (!view) return;
    if (!this.isViewAvailable(view)) return;

    this.pendingInitialView = undefined;
    this.currentView = view;
    this.scrollSelectedTabIntoView();
  }

  /**
   * Bring the selected tab into view in the scrollable strip.
   *
   * Below the tablet breakpoint the strip scrolls rather than squeezing every tab into the width,
   * so a tab selected from the address (`?tab=cards`) or by a deep link can sit off-screen — the
   * page then looks like it opened on nothing. The browser does not do this for us: it scrolls a
   * FOCUSED element into view, and a checked radio inside `mat-button-toggle` is not focused.
   *
   * Sets `scrollLeft` directly rather than calling `scrollIntoView`, which walks every
   * scrollable ancestor and would drag the embedding page around as a side effect.
   */
  // Public: the template calls it on (change) so a tap on a partly-visible tab pulls it in.
  public scrollSelectedTabIntoView(): void {
    // Deferred a frame: this is called as the selection changes, before the DOM reflects it.
    setTimeout(() => {
      const strip = this.tabStrip?.nativeElement;
      if (!strip) return;

      const selected = strip.querySelector<HTMLElement>('.mat-button-toggle-checked');
      if (!selected) return;

      // Nothing to do when the strip is not scrolling — every tab is already visible.
      if (strip.scrollWidth <= strip.clientWidth) return;

      const left = selected.offsetLeft;
      const right = left + selected.offsetWidth;
      const viewLeft = strip.scrollLeft;
      const viewRight = viewLeft + strip.clientWidth;

      if (left < viewLeft) {
        strip.scrollTo({ left, behavior: 'smooth' });
      } else if (right > viewRight) {
        // Aligned to the right edge rather than centred: the tabs before the selected one are the
        // ones somebody is most likely to want next, so keep them in view.
        strip.scrollTo({ left: right - strip.clientWidth, behavior: 'smooth' });
      }
    });
  }

  loadCustomerData() {
    this.customersService.getCustomer(this.customerId).subscribe({
      next: (customer) => {
        this.customerData = customer;
        console.log('[CustomerDetails] Customer data loaded:', this.customerData);
        // The tabs gated on customer data exist now, so a view that could not be applied at init
        // can be.
        setTimeout(() => this.applyPendingInitialView());
      },
      error: (error) => {
        console.error('[CustomerDetails] Error loading customer data:', error);
      },
    });
  }

  ngAfterViewInit(): void {
    // Apply the initial view after templates are ready
    // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => this.applyPendingInitialView());
  }

  loadTransactions() {
    this.transactions$ = this.transactionApiService.getTransactionsByCustomerId(this.customerId);
  }

  loadScheduledPayments() {
    // Subscribe to the BehaviorSubject observable for real-time updates
    this.scheduledPayments$ = this.scheduledPaymentsService.scheduledPayments$;

    // Trigger the initial load
    this.scheduledPaymentsService.getScheduledPayments(this.customerId).subscribe({
      next: () => console.log('[CustomerDetails] Initial scheduled payments loaded'),
      error: (error) => console.error('[CustomerDetails] Error loading scheduled payments:', error),
    });
  }

  loadPaymentMethods() {
    this.customersService.getPaymentMethods(this.customerId).subscribe({
      next: (paymentMethods) => {
        // Filter out ACH accounts (only show credit cards)
        const creditCards = paymentMethods.filter((pm) => pm.tokenType === 'CARD');

        // Clear the form array
        this.isDefaultFormArray.clear();

        // Transform payment methods to include form controls
        const paymentMethodsWithForm = creditCards.map((pm: PaymentMethodModel) => {
          const formControl = new FormControl<boolean>(pm.isDefault ?? false);
          const formGroup = new FormGroup({
            paymentMethodId: new FormControl<string | null>(pm.id ?? null),
            isDefault: formControl,
          });

          this.isDefaultFormArray.push(formGroup);

          // Subscribe to form changes for set default functionality
          formGroup.valueChanges.subscribe((values) => {
            // Uncheck all other checkboxes
            this.isDefaultFormArray.controls.forEach((control) => {
              const paymentMethodId = control.get('paymentMethodId')?.value;
              if (paymentMethodId !== values.paymentMethodId) {
                control.get('isDefault')?.setValue(false, { emitEvent: false });
              }
            });

            // Set as default if checked
            if (values.isDefault && values.paymentMethodId) {
              this.setDefaultPaymentMethod(values.paymentMethodId);
            }
          });

          return {
            ...pm,
            isDefaultFormControl: formControl,
          } as PaymentMethodModelWithForm;
        });

        this.paymentMethodsSubject.next(paymentMethodsWithForm);
      },
      error: (error) => {
        console.error('[CustomerDetails] Error loading payment methods:', error);
        this.notificationService.error('Failed to load payment methods');
      },
    });
  }

  setDefaultPaymentMethod(paymentMethodId: string) {
    this.overlayService.showOverlay();
    this.customersService.setDefaultPaymentMethod(this.customerId, paymentMethodId).subscribe({
      next: () => {
        this.overlayService.hideOverlay();
        this.notificationService.success('Default payment method updated successfully');
        // One default per customer across cards AND bank accounts, so both lists change.
        this.loadPaymentMethods();
        this.loadAchPaymentMethods();
      },
      error: (error) => {
        this.overlayService.hideOverlay();
        this.notificationService.error('Failed to set default payment method');
        console.error('[CustomerDetails] Error setting default payment method:', error);
      },
    });
  }

  public isScheduledPaymentsEnabled(): boolean {
    const featureEnabled =
      this.userStore?.activeProfile()?.profileDetail?.profileAdminSettings?.enableScheduledPayments || false;
    const hasPermission = this.permissionService.hasPermission('SCHEDULED_PAYMENT_LIST');
    return featureEnabled && hasPermission;
  }

  /** Accounts on File exists when the customer can hold either kind of account. */
  public isAccountsOnFileEnabled(): boolean {
    return this.isCardsOnFileEnabled() || this.isAchPayEnabled();
  }

  private toAccountItems(rows: PaymentMethodModel[], rail: 'CARD' | 'ACH'): AccountCardItem[] {
    const now = new Date();
    return rows
      .map((row) => {
        const method = { ...row, tokenType: row.tokenType || rail } as PaymentMethodModel;
        return { method, view: describeAccount(method, now) };
      })
      .sort((a, b) => Number(!!b.method.isDefault) - Number(!!a.method.isDefault));
  }

  accountActionsFor(item: AccountCardItem): GridColumnActionType<PaymentMethodModel>[] {
    const actions = item.view.rail === 'ACH' ? achPaymentMethodActions : paymentMethodActions;
    return actions.filter(
      (action) => !action.canShow || action.canShow(item.method, this.accountActionHost, this.injector)
    );
  }

  runAccountAction(action: GridColumnActionType<PaymentMethodModel>, item: AccountCardItem): void {
    action.handler(item.method, this.accountActionHost, this.injector);
  }

  public isCardsOnFileEnabled(): boolean {
    // If customer data hasn't loaded yet, hide the tab
    if (!this.customerData) {
      return false;
    }

    // Check if customer-specific setting allows credit card payments
    // If customerSettings is null/undefined, default to true (backwards compatibility)
    // If customerSettings exists but acceptCreditCardPayment is undefined, default to true
    // If acceptCreditCardPayment is explicitly false, respect that
    if (!this.customerData.customerSettings) {
      return true; // Backwards compatibility for customers without settings
    }

    const customerAllowsCreditCard = this.customerData.customerSettings.acceptCreditCardPayment ?? true;
    return customerAllowsCreditCard;
  }

  public isAchPayEnabled(): boolean {
    // First check if ACH is enabled at profile level
    const profileAchEnabled =
      this.userStore?.activeProfile()?.profileDetail?.profileAdminSettings?.enableAchPay || false;

    if (!profileAchEnabled) {
      return false;
    }

    // If customer data hasn't loaded yet, hide the tab
    if (!this.customerData) {
      return false;
    }

    // Check if customer-specific setting allows ACH payments
    // If customerSettings is null/undefined, default to true (backwards compatibility)
    // If customerSettings exists but acceptACHPayment is undefined, default to true
    // If acceptACHPayment is explicitly false, respect that
    if (!this.customerData.customerSettings) {
      return true; // Backwards compatibility for customers without settings
    }

    const customerAllowsACH = this.customerData.customerSettings.acceptACHPayment ?? true;
    return customerAllowsACH;
  }

  public isInvoiceEnabled(): boolean {
    return this.userStore?.activeProfile()?.profileDetail?.profileAdminSettings?.enableInvoice || false;
  }

  /**
   * The customer ledger exists only for a profile that runs an ERP AND invoicing —
   * FTE's `isCustomerLedgerEnabled`. `erpEnabled` is FTE's derived flag (the
   * integration switch AND a chosen application); either half alone is meaningless.
   */
  public isCustomerLedgerEnabled(): boolean {
    const detail = this.userStore?.activeProfile()?.profileDetail;
    return !!detail?.erpEnabled && this.isInvoiceEnabled();
  }

  /** The Ledger tab: the ledger exists AND the reader may see it (the ledger reads need GET_TRANSACTION_STATS). */
  public canViewLedger(): boolean {
    return this.isCustomerLedgerEnabled() && this.permissionService.hasPermission('GET_TRANSACTION_STATS');
  }

  /**
   * Show the customer's **FreelyPayments** instead of their gateway
   * transactions.
   *
   * The existing "Customer Payments" grid reads `DBTransactions`, so it shows
   * only what Freely processed — on an ERP profile most of the money arrived
   * as an import and was simply absent from the customer's own page. The same
   * gate as the Ledger tab (`enableInvoice` AND `erpEnabled`), because that is
   * exactly when the two views diverge: without an ERP every payment has a
   * transaction behind it and the old grid is equivalent, and swapping it
   * would cost the per-transaction columns for nothing.
   */
  public showFreelyPayments(): boolean {
    return this.isCustomerLedgerEnabled();
  }

  /**
   * A payment-detail reader asked to see the invoice or the ledger entry
   * behind a payment. Reuses the tab switch the Invoices tab already uses, so
   * arriving from a payment and arriving from an invoice land the same way.
   */
  handlePaymentNavigation(request: PaymentNavigationRequest): void {
    if (request.target === 'invoice' && request.invoiceRef) {
      this.invoiceFocusRef = request.invoiceRef;
      this.currentView = 'invoices';
      return;
    }
    if (request.target === 'ledger') {
      // Narrowed to the document when the entry names one; otherwise the whole
      // statement, which is still far closer than making them find the tab.
      this.ledgerDocumentRef = request.invoiceRef ?? null;
      this.currentView = 'ledger';
    }
  }

  /** The invoice the Invoices tab should scroll to / highlight on arrival. */
  invoiceFocusRef: string | null = null;

  /** The document the Ledger tab opens narrowed to, when arriving from an invoice. */
  ledgerDocumentRef: string | null = null;

  /** From the Invoices tab: show this document's ledger entries. */
  handleViewInvoiceLedger(invoiceRef: string): void {
    this.ledgerDocumentRef = invoiceRef;
    this.currentView = 'ledger';
    this.scrollSelectedTabIntoView();
  }

  public isCustomerPortalEnabled(): boolean {
    return this.userStore?.activeProfile()?.profileDetail?.profileAdminSettings?.enableCustomerPortal || false;
  }

  errorMessages = {
    contactFirstName: {
      required: 'First name is required',
      minlength: 'First name must be at least 2 characters',
      maxlength: 'First name cannot exceed 40 characters',
      pattern: 'First name can only contain letters and spaces',
    },
    contactLastName: {
      required: 'Last name is required',
      minlength: 'Last name must be at least 2 characters',
      maxlength: 'Last name cannot exceed 40 characters',
      pattern: 'Last name can only contain letters and spaces',
    },
    companyName: {
      required: 'Customer name is required',
      minlength: 'Customer name must be at least 2 characters',
      maxlength: 'Customer name cannot exceed 40 characters',
    },
    displayName: {
      required: 'Display name is required',
    },
    companyAccountNumber: {
      required: 'Account number is required',
      pattern: 'Account number can only contain letters, numbers, and hyphens',
    },
    businessPhone: {
      pattern: 'Invalid phone number',
    },
    businessPhoneExt: {
      pattern: 'Extension can only contain up to 10 digits',
    },
    mobilePhoneNumber: {
      pattern: 'Please enter a valid mobile number',
    },
    contactEmail: {
      //email: 'Please enter a valid email address',
      pattern: 'Please enter a valid email address',
    },
    notes: {
      maxlength: 'Notes cannot exceed 800 characters',
    },
  };

  templateRefs(): { [key: string]: TemplateRef<any> } {
    return {
      voidTemplate: this.voidTemplate,
      settledTemplate: this.settledTemplate,
      flagsTemplate: this.flagsTemplate,
      tranTypeTemplate: this.tranTypeTemplate,
      transIdTemplate: this.transIdTemplate,
      statusTemplate: this.statusTemplate,
      cardType: this.cardTypeTemplate,
      setDefault: this.setDefaultTemplate,
      invoiceStatusTemplate: this.invoiceStatusTemplate,
      invoiceActionsTemplate: this.invoiceActionsTemplate,
    };
  }
  newPayment() {
    if (this.customerId) {
      window.location.href = `/payment/collect?customerId=${encodeURIComponent(this.customerId)}`;
    }
  }

  downloadFullCSV() {
    if (this.transactionsGrid) {
      this.transactionsGrid.onDownloadFullCSV();
    }
  }
  downloadFilteredPDF() {
    if (this.transactionsGrid) {
      this.transactionsGrid.onDownloadFilteredPDF();
    }
  }
  downloadFilteredCSV() {
    if (this.transactionsGrid) {
      this.transactionsGrid.onDownloadFilteredCSV();
    }
  }

  newScheduledPayment() {
    this.modalService.openModal({
      title: 'New Scheduled Payment',
      body: ScheduledPaymentFormComponent,
      data: {
        customerId: this.customerId,
      },
      confirm: {
        label: 'Create Schedule',
        action: (modal, formComponent: ScheduledPaymentFormComponent) => {
          const form = formComponent.form;
          if (!form.valid) {
            form.markAllAsTouched();
            return;
          }

          const requestData = formComponent.getFormData();

          this.scheduledPaymentsService.createScheduledPayment(this.customerId, requestData).subscribe({
            next: (created) => {
              console.log('[CustomerDetails] Scheduled payment created:', created);
              modal.close();
            },
            error: (error) => {
              console.error('[CustomerDetails] Error creating scheduled payment:', error);
            },
          });
        },
      },
      dismiss: {
        label: 'Cancel',
        action: (modal: ModalComponentInterface) => {
          modal.close();
        },
      },
    });
  }

  openAddCardModal() {
    this.modalService.openModal({
      title: 'Add Payment Card',
      body: CardDetailsFormComponent,
      data: {},
      confirm: {
        label: 'Save',
        action: (modal: ModalComponentInterface, cardDetailsFormComponent: CardDetailsFormComponent) => {
          if (cardDetailsFormComponent.isVerificationPending) {
            cardDetailsFormComponent.invalidMessage = 'Card verification in progress — please wait';
            return;
          }
          const form = cardDetailsFormComponent.form;
          if (!form.valid || !cardDetailsFormComponent.isFullyValidated) {
            form.markAllAsTouched();
            if (!form.valid) {
              const failingFields: string[] = [];
              if (form.controls.account.invalid) failingFields.push('Card Number');
              if (form.controls.expirationDate.invalid) {
                if (form.controls.expirationDate.hasError('expirationDateInvalid'))
                  failingFields.push('Expiration Date (expired)');
                else if (form.controls.expirationDate.hasError('expirationDateTooFar'))
                  failingFields.push('Expiration Date (too far in future)');
                else failingFields.push('Expiration Date');
              }
              if (form.controls.cvv.invalid) failingFields.push('CVV');
              if (form.controls.cardHolderName.invalid) failingFields.push('Card Holder Name');
              if (form.controls.streetAddress.invalid) failingFields.push('Street Address');
              if (form.controls.zipCode.invalid) failingFields.push('Zip Code');
              cardDetailsFormComponent.invalidMessage = failingFields.length
                ? `Please fix: ${failingFields.join(', ')}`
                : 'Please complete all required fields';
            } else if (!cardDetailsFormComponent.isFullyValidated) {
              cardDetailsFormComponent.invalidMessage = 'Card must be verified before saving';
            }
            return;
          }

          const paymentMethodModel = cardDetailsFormComponent.getFormData();

          console.log('paymentMethodModel', paymentMethodModel);

          this.cryptService
            .encryptData(paymentMethodModel)
            .then((encryptedPaymentMethod) => {
              const encryptedPaymentMethodModel: PaymentMethodModel = {
                encryptedPaymentAccount: encryptedPaymentMethod,
              };

              this.customersService
                .createPaymentMethod(this.customerId, encryptedPaymentMethodModel)
                .pipe(
                  tap(() => this.overlayService.showOverlay()),
                  switchMap((response) => {
                    if (paymentMethodModel.isDefault) {
                      return this.customersService.setDefaultPaymentMethod(this.customerId, <string>response.id);
                    }
                    return of(null);
                  })
                )
                .subscribe({
                  next: () => {
                    this.loadPaymentMethods();
                    modal.close();
                    this.overlayService.hideOverlay();
                    this.notificationService.success('Payment card created successfully');
                  },
                  error: (error) => {
                    this.overlayService.hideOverlay();
                    const details = error?.data?.details || {};
                    const verbiage = error?.data?.message || '';
                    const suffix = verbiage ? ` — ${verbiage}` : '';

                    if (details.validCVV === false) {
                      cardDetailsFormComponent.invalidMessage = `Invalid CVV${suffix}`;
                      cardDetailsFormComponent.isCvvInvalid = true;
                      cardDetailsFormComponent.hasCvvBeenChecked = true;
                      cardDetailsFormComponent.showIgnoreCVV = true;
                      cardDetailsFormComponent.form.controls.cvv.updateValueAndValidity();
                      cardDetailsFormComponent.cvvInput.nativeElement.focus();
                    } else if (details.validAVSStreet === false || details.validAVSZip === false) {
                      cardDetailsFormComponent.invalidMessage = `Invalid Address/ZIP${suffix}`;
                      cardDetailsFormComponent.isAvsInvalid = true;
                      cardDetailsFormComponent.isStreetInvalid = details.validAVSStreet === false;
                      cardDetailsFormComponent.isZipInvalid = details.validAVSZip === false;
                      cardDetailsFormComponent.hasStreetBeenChecked = true;
                      cardDetailsFormComponent.hasZipBeenChecked = true;
                      if (cardDetailsFormComponent.isStreetInvalid) cardDetailsFormComponent.showIgnoreStreet = true;
                      if (cardDetailsFormComponent.isZipInvalid) cardDetailsFormComponent.showIgnoreZip = true;
                      cardDetailsFormComponent.form.controls.streetAddress.updateValueAndValidity();
                      cardDetailsFormComponent.form.controls.zipCode.updateValueAndValidity();
                      cardDetailsFormComponent.addressInput.nativeElement.focus();
                    } else if (details.validCard === false) {
                      cardDetailsFormComponent.invalidMessage = cardDeclinedMessage(verbiage);
                      cardDetailsFormComponent.isCardInvalid = true;
                      cardDetailsFormComponent.form.controls.account.updateValueAndValidity();
                      cardDetailsFormComponent.accountInput.nativeElement.focus();
                    } else if (verbiage) {
                      cardDetailsFormComponent.invalidMessage = verbiage;
                      cardDetailsFormComponent.isCardInvalid = true;
                      cardDetailsFormComponent.form.controls.account.updateValueAndValidity();
                    } else {
                      cardDetailsFormComponent.invalidMessage =
                        'An error occurred while creating the payment card. Please try again.';
                      cardDetailsFormComponent.form.updateValueAndValidity();
                    }
                  },
                });
            })
            .catch((error) => {
              console.error('Encryption failed:', error);
              this.notificationService.error('Failed to encrypt payment card data');
              this.overlayService.hideOverlay();
            });
        },
      },
      dismiss: {
        label: 'Cancel',
        action: (modal: ModalComponentInterface) => {
          modal.close();
        },
      },
    });
  }

  loadAchPaymentMethods() {
    this.customersService.getAchPaymentMethods(this.customerId).subscribe({
      next: (achMethods) => {
        // Clear the form array
        this.achDefaultFormArray.clear();

        // Transform ACH methods to include form controls
        const achMethodsWithForm = achMethods.map((am: AchMethodModel) => {
          const formControl = new FormControl<boolean>(am.isDefault ?? false);
          const formGroup = new FormGroup({
            paymentMethodId: new FormControl<string | null>(am.id ?? null),
            isDefault: formControl,
          });

          this.achDefaultFormArray.push(formGroup);

          // Subscribe to form changes for set default functionality
          formGroup.valueChanges.subscribe((values) => {
            // Uncheck all other checkboxes
            this.achDefaultFormArray.controls.forEach((control) => {
              const paymentMethodId = control.get('paymentMethodId')?.value;
              if (paymentMethodId !== values.paymentMethodId) {
                control.get('isDefault')?.setValue(false, { emitEvent: false });
              }
            });

            // Set as default if checked
            if (values.isDefault && values.paymentMethodId) {
              this.setDefaultPaymentMethod(values.paymentMethodId);
            }
          });

          return {
            ...am,
            isDefaultFormControl: formControl,
          } as AchMethodModelWithForm;
        });

        this.achMethodsSubject.next(achMethodsWithForm);
      },
      error: (error) => {
        console.error('[CustomerDetails] Error loading ACH payment methods:', error);
        this.notificationService.error('Failed to load ACH payment methods');
      },
    });

    // Set up the observable
    this.achMethods$ = this.achMethodsSubject.asObservable();
  }

  loadInvoices() {
    this.invoices$ = this.customersService.getCustomerInvoices(this.customerId);
  }

  loadPortalUsers() {
    this.portalUsers$ = this.customersService.getCustomerPortalUsers(this.customerId);
  }

  collectPayment(invoice: InvoiceModel): void {
    // Close the modal first
    if (this.parentModal) {
      this.parentModal.close();
    }

    // Navigate to payment collection page with pre-filled data
    this.router.navigate(['/payment/collect'], {
      queryParams: {
        customerId: this.customerId,
        invoiceNumber: invoice.invoiceNumber,
        amount: invoice.amount,
      },
    });
  }

  schedulePayment(invoice: InvoiceModel): void {
    // Open scheduled payment modal with invoice context
    // Reuse existing newScheduledPayment() method
    this.newScheduledPayment();
    // TODO: Pre-fill modal with invoice data when backend supports it
  }

  openAddAchModal() {
    this.modalService.openModal({
      title: 'Add ACH Account',
      body: AchDetailsFormComponent,
      data: {},
      confirm: {
        label: 'Save',
        action: (modal: ModalComponentInterface, achDetailsFormComponent: AchDetailsFormComponent) => {
          const form = achDetailsFormComponent.form;
          if (!form.valid) {
            form.markAllAsTouched();
            return;
          }

          const achMethodModel = achDetailsFormComponent.getFormData();

          console.log('achMethodModel', achMethodModel);

          this.cryptService
            .encryptData(achMethodModel)
            .then((encryptedAchMethod) => {
              const encryptedAchMethodModel: AchMethodModel = {
                encryptedPaymentAccount: encryptedAchMethod,
              };

              this.customersService
                .createACHPaymentMethod(this.customerId, encryptedAchMethodModel)
                .pipe(
                  tap(() => this.overlayService.showOverlay()),
                  switchMap((response) => {
                    if (achMethodModel.isDefault) {
                      return this.customersService.setDefaultPaymentMethod(this.customerId, <string>response.id);
                    }
                    return of(null);
                  })
                )
                .subscribe({
                  next: () => {
                    this.loadAchPaymentMethods();
                    modal.close();
                    this.overlayService.hideOverlay();
                    this.notificationService.success('ACH account created successfully');
                  },
                  error: (error) => {
                    this.overlayService.hideOverlay();
                    console.error('[CustomerDetails] Error creating ACH account:', error);

                    // Handle specific ACH configuration errors
                    if (error.data?.details?.code === 'SETUP_CARDTYPE') {
                      this.notificationService.error(
                        'ACH payments are not configured for this merchant profile. Please contact support to enable ACH processing.'
                      );
                    } else if (error.data?.details?.code === 'FAILED TOKEN CREATE') {
                      const processorCode = error.data?.details?.processor?.msoft_code;
                      const verbiage = error.data?.details?.processor?.verbiage || '';

                      if (processorCode === 'DATA_BADTRANS' && verbiage.includes('ACH not supported')) {
                        this.notificationService.error(
                          'ACH payments are not enabled for your merchant account at the processor level. Please contact your payment processor or support to enable ACH processing.'
                        );
                      } else if (verbiage) {
                        this.notificationService.error(`Unable to create ACH account: ${verbiage}`);
                      } else {
                        this.notificationService.error(
                          `Failed to create ACH token. Error code: ${processorCode || error.code}`
                        );
                      }
                    } else if (error.data?.details?.code === 'ACH_VALIDATION_FAILED') {
                      const processorMessage = error.data?.details?.processor?.verbiage || error.message;
                      this.notificationService.error(`ACH validation failed: ${processorMessage}`);
                    } else {
                      let message = `An error occurred while creating ACH account. Please try again later. Error code: ${error.code}`;
                      this.notificationService.error(message);
                    }
                  },
                });
            })
            .catch((error) => {
              console.error('Encryption failed:', error);
              this.notificationService.error('Failed to encrypt ACH account data');
              this.overlayService.hideOverlay();
            });
        },
      },
      dismiss: {
        label: 'Cancel',
        action: (modal: ModalComponentInterface) => {
          modal.close();
        },
      },
    });
  }

  addPortalUser() {
    const NewPortalUserComponent = import('../new-portal-user/new-portal-user.component').then(
      (m) => m.NewPortalUserComponent
    );

    NewPortalUserComponent.then((component) => {
      this.modalService.openModal({
        title: 'Add Portal User',
        body: component,
        data: {},
        confirm: {
          label: 'Create User',
          action: (modal: ModalComponentInterface, newPortalUserComponent: any) => {
            const form = newPortalUserComponent.form;
            if (!form.valid) {
              form.markAllAsTouched();
              return;
            }

            const selectedRoles = newPortalUserComponent.getSelectedRoles();
            if (selectedRoles.length === 0) {
              this.notificationService.error('Please select at least one role for the user');
              return;
            }

            const profileId = this.userStore.activeProfile()?.profileDetail?.profileId;
            if (!profileId) {
              this.notificationService.error('No active profile selected');
              return;
            }

            // Prepare user data
            const newUser = {
              userId: form.controls.userId.value?.toLowerCase() || '',
              firstName: form.controls.firstName.value || '',
              lastName: form.controls.lastName.value || '',
              emailAddress: form.controls.emailAddress.value || '',
              mobileNumber: form.controls.mobileNumber.value
                ? form.controls.mobileNumber.value.replace(/\D/g, '')
                : undefined,
              googleAuthEnabled: false,
              isDisabled: false,
              userSettings: {},
              defaultProfile: profileId,
              initialPassword: form.controls.password.value || '',
              userType: 'CUSTOMER',
              initialRole: selectedRoles[0], // Backend expects single role, but we'll update with all roles after creation
            };

            this.overlayService.showOverlay();

            // Create the user
            this.customersService.createCustomerPortalUser(this.customerId, newUser).subscribe({
              next: (createdUser) => {
                // Now set all the selected roles
                this.customersService
                  .setCustomerPortalUserRoles(this.customerId, createdUser.userNumber, selectedRoles)
                  .subscribe({
                    next: () => {
                      this.overlayService.hideOverlay();
                      this.notificationService.success('Portal user created successfully');
                      this.loadPortalUsers(); // Reload the grid
                      modal.close();
                    },
                    error: (roleError) => {
                      this.overlayService.hideOverlay();
                      // User was created but roles failed - still show success but warn
                      this.notificationService.success(
                        'User created successfully, but there was an issue assigning roles. Please edit user roles manually.'
                      );
                      this.loadPortalUsers();
                      modal.close();
                      console.error('[CustomerDetails] Error setting roles:', roleError);
                    },
                  });
              },
              error: (error) => {
                this.overlayService.hideOverlay();
                console.error('[CustomerDetails] Error creating portal user:', error);
                if (error.data?.message) {
                  this.notificationService.error(`User creation failed: ${error.data.message}`);
                } else {
                  this.notificationService.error(`User creation failed: ${error.message}`);
                }
              },
            });
          },
        },
        dismiss: {
          label: 'Cancel',
          action: (modal: ModalComponentInterface) => {
            modal.close();
          },
        },
      });
    });
  }

  /**
   * A tab was clicked.
   *
   * One-way bound rather than `[(value)]` so the switch can be refused: leaving Customer Info with
   * unsaved edits asks the operator to save or discard them first. The toggle group has already
   * moved its own selection by the time this fires, so a refused switch puts it back explicitly —
   * `currentView` did not change, so the binding alone would not.
   */
  async onViewChange(event: MatButtonToggleChange): Promise<void> {
    const target = event.value as typeof this.currentView;
    if (target === this.currentView) return;

    if (this.currentView === 'editor' && this.editor?.hasUnsavedChanges()) {
      if (this.tabGroup) this.tabGroup.value = this.currentView;
      if (!(await this.canLeaveEditor())) {
        this.scrollSelectedTabIntoView();
        return;
      }
    }

    this.currentView = target;
    this.scrollSelectedTabIntoView();
  }

  /**
   * Resolves true when nothing would be lost by leaving the Customer Info tab — no editor open,
   * no edits in it, or the operator saved or discarded them.
   */
  canLeaveEditor(): Promise<boolean> {
    return this.editor ? this.editor.confirmLeave() : Promise.resolve(true);
  }

  /** Asked by the bottom sheet before it closes (X, Escape or a click outside it). */
  canDismiss(): Promise<boolean> {
    return this.canLeaveEditor();
  }

  handleEditorClose(): void {
    // Reload customer data to reflect any changes made in the editor
    this.loadCustomerData();
    // Switch back to transactions view after save or cancel
    this.currentView = 'transactions';
  }

  handleInvoiceCollectClicked(): void {
    // Close the modal when collect button is clicked from invoice history
    if (this.parentModal) {
      this.parentModal.close();
    }
  }
}
